// Hợp đồng của inventory-service (đặc tả docs/backend/services/inventory-service.md): dự án, chính sách bán hàng &
// hoa hồng, danh mục căn, lịch sử giá, tạo nhanh / nhập bảng hàng, xoá (saga nhỏ với booking).
// Quy ước: tiền VND số nguyên (number, ≤ 9e15); phần trăm là number; thời điểm ISO 8601 UTC; id UUID.
// Thông tin chủ nhà ký gửi (ownerName/ownerPhone) KHÔNG bao giờ có trong sự kiện, danh sách, tìm kiếm.
import { z } from 'zod';
import { fold } from '../text.js';

export * from './showroom.js';

// ---------- Bộ dựng schema (thông điệp tiếng Việt) ----------
const REQUIRED = 'Thiếu dữ liệu bắt buộc';
const msg = (message: string) => ({ errorMap: () => ({ message }) });
/** Mọi chuỗi vào: cấm ký tự NUL (PostgreSQL text không lưu được ⇒ lỗi CSDL thay vì 400 — U8: không để câu SQL lỗi mang dữ liệu nhạy cảm). */
const str = (required = REQUIRED) => z.string({ required_error: required, invalid_type_error: 'Phải là chuỗi ký tự' }).regex(/^[^\u0000]*$/, 'Chứa ký tự không hợp lệ');
const text = (max = 200) => str().trim().max(max, `Tối đa ${max} ký tự`);
const reqText = (max: number, message: string) => str(message).trim().min(1, message).max(max, `Tối đa ${max} ký tự`);
const uuid = str().uuid('Mã định danh không hợp lệ');
const num = (required = REQUIRED) => z.number({ required_error: required, invalid_type_error: 'Phải là số' });
const int = (required = REQUIRED) => num(required).int('Phải là số nguyên');
const version = int().min(0, 'Phiên bản không hợp lệ');
const money = num('Nhập số tiền').int('Số tiền phải là số nguyên').min(0, 'Số tiền không được âm').max(9e15, 'Số tiền vượt giới hạn');
const bool = z.boolean({ required_error: REQUIRED, invalid_type_error: 'Phải là đúng/sai' });
const decimals = (d: number) => (x: number) => Math.abs(x * 10 ** d - Math.round(x * 10 ** d)) < 1e-6;
const pct = (d = 2) => num().min(0, 'Phần trăm không được âm').max(100, 'Phần trăm tối đa 100').refine(decimals(d), `Tối đa ${d} chữ số thập phân`);
const isoTime = str().datetime({ offset: true, message: 'Thời điểm phải theo ISO 8601' });
const qInt = (min: number, max: number, def?: number) => {
  const base = z.coerce.number({ invalid_type_error: 'Phải là số' }).int('Phải là số nguyên').min(min, `Tối thiểu ${min}`).max(max, `Tối đa ${max}`);
  return def === undefined ? base.optional() : base.default(def);
};
const boolish = (def?: boolean) =>
  z.preprocess(
    (v) => (v === 'true' || v === '1' || v === true ? true : v === 'false' || v === '0' || v === false ? false : def),
    def === undefined ? z.boolean().optional() : z.boolean(),
  );
const optText = (max = 200) => z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? null : v), text(max).nullish());
/** "A, B" hoặc ['A','B'] ⇒ mảng đã trim, bỏ rỗng, bỏ trùng giữ thứ tự (đặc tả §1.3). */
const splitComma = (v: unknown) => {
  const list = typeof v === 'string' ? v.split(',') : Array.isArray(v) ? v : null;
  if (!list) return v;
  const out: unknown[] = [];
  for (const x of list) {
    const t = typeof x === 'string' ? x.trim() : x;
    if (t !== '' && !out.includes(t)) out.push(t);
  }
  return out;
};

// ---------- Hằng số (nhãn hiển thị ở packages/domain) ----------
export const PROJECT_TYPES = ['Căn hộ', 'Nhà phố', 'Biệt thự', 'Shophouse', 'Đất nền', 'Condotel'] as const;
export type ProjectType = (typeof PROJECT_TYPES)[number];
export const PROJECT_STATUSES = ['upcoming', 'selling', 'paused', 'closed'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];
export const MARKETS = ['primary', 'secondary'] as const;
export type Market = (typeof MARKETS)[number];
export const LEGAL_TYPES = ['Sổ hồng riêng', 'Sổ đỏ', 'HĐMB với CĐT', 'Đang chờ sổ', 'Giấy phép xây dựng', 'Văn bản đủ điều kiện bán nhà hình thành trong tương lai'] as const;
export const FLOOR_LABELS = ['Tầng', 'Dãy', 'Khu vực'] as const;
export type FloorLabel = (typeof FLOOR_LABELS)[number];
export const MILESTONE_TYPES = ['deposit', 'contract', 'paidPct', 'devPaid'] as const;
export type MilestoneType = (typeof MILESTONE_TYPES)[number];
export const UNIT_SOURCES = ['manual', 'generator', 'import'] as const;
export type UnitSource = (typeof UNIT_SOURCES)[number];
export const PRICE_CHANGE_SOURCES = ['manual', 'bulk_adjust', 'import'] as const;
export type PriceChangeSource = (typeof PRICE_CHANGE_SOURCES)[number];
/** Trạng thái hiệu lực của căn (mirror từ booking; giữ quá hạn ⇒ open). */
export const UNIT_STATUSES = ['open', 'locked', 'pending', 'deposited', 'contracted', 'blocked'] as const;
export type UnitStatus = (typeof UNIT_STATUSES)[number];
export const LIFECYCLES = ['active', 'deleting'] as const;
export type Lifecycle = (typeof LIFECYCLES)[number];
/** INVALID_ROW (bổ sung production): dòng thêm mới có ô vượt giới hạn của căn (mã ≤ 64, toà ≤ 40, loại/hướng ≤ 60, view ≤ 100, địa chỉ ≤ 300, tầng ≤ 500, vị trí ≤ 1.000, diện tích ≤ 1.000.000). */
/** INFO_UPDATE: căn đã có, giá không đổi nhưng quỹ / trục / giá theo phương án / thông tin khác đổi (cập nhật quỹ hằng ngày). */
export const IMPORT_CLASSIFICATIONS = ['MISSING_CODE', 'MISSING_PRICE', 'HAS_DEAL', 'UNCHANGED', 'PRICE_UPDATE', 'INFO_UPDATE', 'CREATE', 'DUPLICATE_IN_FILE', 'INVALID_ROW'] as const;
export type ImportClassification = (typeof IMPORT_CLASSIFICATIONS)[number];
export const IMPORT_CLASSIFICATION_LABEL: Record<ImportClassification, string> = {
  MISSING_CODE: 'Thiếu mã',
  MISSING_PRICE: 'Thiếu giá',
  HAS_DEAL: 'Đã có giao dịch — bỏ qua',
  UNCHANGED: 'Không đổi',
  PRICE_UPDATE: 'Cập nhật giá',
  INFO_UPDATE: 'Cập nhật quỹ / thông tin',
  CREATE: 'Thêm mới',
  DUPLICATE_IN_FILE: 'Trùng mã trong file — bỏ qua',
  INVALID_ROW: 'Dữ liệu vượt giới hạn — bỏ qua',
};
export const DELETION_KINDS = ['unit', 'project'] as const;
export type DeletionKind = (typeof DELETION_KINDS)[number];
export const DELETION_STATES = ['pending', 'completed', 'rejected', 'stalled'] as const;
export type DeletionState = (typeof DELETION_STATES)[number];
export const UNIT_SORTS = ['code', 'type', 'area', 'price', 'ppm'] as const;
/** Chiết khấu tối đa của phiếu tính giá nhanh (kẹp như giao diện). */
export const QUOTE_DISCOUNT_MAX = 20;
/** Khoá idempotency bước rút căn/dự án ở booking. */
export const withdrawKey = (deletionRequestId: string) => `${deletionRequestId}:withdraw`;

/** Số toà / phân khu tối đa của một dự án (P8) — tạo nhanh / nhập file không nối quá ngưỡng này. */
export const MAX_BLOCKS = 100;

// ---------- Bảng hàng CĐT: quỹ căn, phương án giá, tài liệu (docs/NHAN-HANG-DU-AN.md) ----------
/**
 * Quỹ căn theo bảng hàng CĐT / F1 — khác trạng thái giữ căn của booking (sàn mình đang giữ / cọc):
 *  exclusive  Độc quyền của sàn (bán ngay)          shared  Quỹ chéo — các đại lý cùng bán (bán ngay, chốt trước được trước)
 *  hidden     Quỹ ẩn / thu quỹ — hỏi Admin dự án trước khi giữ
 *  sold       Đã bán trên thị trường (CĐT / đại lý khác) — không giữ được
 *  unreleased Chưa mở quỹ — không giữ được
 * null = dự án không quản lý theo quỹ (mọi căn bán bình thường).
 */
export const UNIT_FUNDS = ['exclusive', 'shared', 'hidden', 'sold', 'unreleased'] as const;
export type UnitFund = (typeof UNIT_FUNDS)[number];
export const UNIT_FUND_LABEL: Record<UnitFund, string> = {
  exclusive: 'Độc quyền',
  shared: 'Quỹ chéo',
  hidden: 'Quỹ ẩn',
  sold: 'Đã bán',
  unreleased: 'Chưa mở quỹ',
};
/** Quỹ không giữ căn được (booking chặn LockUnit, không làm căn đích đổi căn). */
export const UNSELLABLE_FUNDS: readonly UnitFund[] = ['sold', 'unreleased'];
export const isSellableFund = (f: UnitFund | null | undefined) => !f || !UNSELLABLE_FUNDS.includes(f);
/**
 * Nhận diện quỹ từ ô "Tình trạng" của bảng hàng CĐT (đã fold + trim). Không nhận ra ⇒ undefined (giữ nguyên quỹ cũ);
 * ô trống ⇒ null (không phân loại).
 */
export function fundFromText(raw: string): UnitFund | null | undefined {
  const t = fold(raw).trim().replace(/\s+/g, ' ');
  if (!t) return null;
  if (/^(doc quyen|dq|dqtc|doc quyen tu chon)\b/.test(t)) return 'exclusive';
  if (/^(quy cheo|qc|cheo)\b/.test(t)) return 'shared';
  if (/^(quy an|thu quy|check ad)\b/.test(t)) return 'hidden';
  if (/^(da ban|sold|ban roi|da coc|da ky)\b/.test(t)) return 'sold';
  if (/^(chua mo|chua mo quy|chua mo ban|khoa|lock)\b/.test(t)) return 'unreleased';
  if (/^(con|con trong|mo ban|dang ban|available)\b/.test(t)) return null;
  return undefined;
}

/** Phương án thanh toán có sẵn — khoá giá trong UnitAttrs.prices; nhãn và bí danh tiêu đề cột của bảng hàng CĐT (đã fold). */
export const PRICE_SCHEME_PRESETS = [
  { key: 'tttd', label: 'TT tiêu chuẩn', aliases: ['gia tttd', 'gia tt tieu chuan', 'tt tieu chuan', 'thanh toan tieu chuan', 'gia ttc', 'tt chuan', 'phuong an tt chuan', 'gia thanh toan tieu chuan'] },
  { key: 'tts', label: 'TT sớm', aliases: ['gia tts', 'tt som', 'thanh toan som', 'gia thanh toan som', 'phuong an tt som'] },
  { key: 'vay', label: 'Vay HTLS', aliases: ['gia vay', 'vay htls', 'gia htls', 'htls', 'gia vay ngan hang'] },
  { key: 'vay70', label: 'Vay 70%', aliases: ['vay 70%', 'gia vay 70%', 'phuong an vay 70%', 'patt vay 70%'] },
  { key: 'vay50', label: 'Vay 50%', aliases: ['vay 50%', 'gia vay 50%', 'phuong an vay 50%', 'patt vay 50%'] },
] as const;
export type PriceSchemePreset = (typeof PRICE_SCHEME_PRESETS)[number]['key'];
export const PRICE_SCHEME_KEY = /^[a-z][a-z0-9_]{0,23}$/;
/** Giới hạn của thuộc tính bảng hàng (kiểm ở hợp đồng và khi nhập file). */
export const ATTR_LIMITS = { axis: 12, zone: 60, agency: 80, schemes: 8, unitDocs: 10, projectDocs: 120, payInfo: 10 } as const;

export const UNIT_DOC_KINDS = ['ptg', 'plan', 'other'] as const;
export type UnitDocKind = (typeof UNIT_DOC_KINDS)[number];
export const UNIT_DOC_KIND_LABEL: Record<UnitDocKind, string> = { ptg: 'Phiếu tính giá', plan: 'Chỉ căn / mặt bằng căn', other: 'Tài liệu' };
/** Nhóm tài liệu dự án — thứ tự hiển thị ở tab Tài liệu. */
export const PROJECT_DOC_CATEGORIES = ['policy', 'price', 'layout', 'contract', 'handover', 'brochure', 'media', 'training', 'other'] as const;
export type ProjectDocCategory = (typeof PROJECT_DOC_CATEGORIES)[number];
export const PROJECT_DOC_CATEGORY_LABEL: Record<ProjectDocCategory, string> = {
  policy: 'Chính sách bán hàng',
  price: 'Bảng giá & phiếu tính giá',
  layout: 'Mặt bằng & layout',
  contract: 'Mẫu hợp đồng & pháp lý',
  handover: 'Tiêu chuẩn bàn giao',
  brochure: 'Tài liệu bán hàng',
  media: 'Phối cảnh & thư viện ảnh',
  training: 'Đào tạo',
  other: 'Khác',
};

// ---------- Dữ liệu vào: thuộc tính bảng hàng CĐT & bộ tài liệu dự án ----------
const webUrl = str()
  .trim()
  .max(1000, 'Đường dẫn tối đa 1.000 ký tự')
  .regex(/^https?:\/\/\S+$/i, 'Đường dẫn phải bắt đầu bằng http:// hoặc https://');
const isoDate = str().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ngày theo dạng YYYY-MM-DD');
export const unitDocInput = z.object({
  kind: z.enum(UNIT_DOC_KINDS, msg('Loại tài liệu căn không hợp lệ')),
  title: reqText(120, 'Nhập tên tài liệu'),
  url: webUrl,
});
const schemePrices = z
  .record(str().regex(PRICE_SCHEME_KEY, 'Mã phương án giá không hợp lệ'), money)
  .refine((r) => Object.keys(r).length <= ATTR_LIMITS.schemes, `Tối đa ${ATTR_LIMITS.schemes} phương án giá`);
/**
 * Thuộc tính bảng hàng của căn (CĐT / F1): trục, phân khu, quỹ, đại lý giữ quỹ, giá theo phương án thanh toán, giá thuần,
 * tài liệu của căn (phiếu tính giá, chỉ căn). Gửi ⇒ THAY toàn bộ thuộc tính (vắng mặt = giữ nguyên).
 */
export const unitAttrsInput = z
  .object({
    axis: optText(ATTR_LIMITS.axis),
    zone: optText(ATTR_LIMITS.zone),
    fund: z.enum(UNIT_FUNDS, msg('Quỹ căn không hợp lệ')).nullish(),
    agency: optText(ATTR_LIMITS.agency),
    prices: schemePrices.optional(),
    netPrice: money.nullish(),
    docs: z.array(unitDocInput).max(ATTR_LIMITS.unitDocs, `Tối đa ${ATTR_LIMITS.unitDocs} tài liệu mỗi căn`).optional(),
  })
  .strict();
export const priceSchemeInput = z.object({
  key: str().regex(PRICE_SCHEME_KEY, 'Mã phương án: chữ thường, số, gạch dưới (tối đa 24 ký tự)'),
  label: reqText(40, 'Nhập tên phương án'),
});
export const projectDocInput = z
  .object({
    category: z.enum(PROJECT_DOC_CATEGORIES, msg('Nhóm tài liệu không hợp lệ')),
    title: reqText(160, 'Nhập tên tài liệu'),
    url: webUrl,
    /** Tài liệu riêng của một toà (layout, mặt bằng tầng). */
    block: optText(40),
    /** Hiệu lực (chính sách bán hàng, phiếu tính giá): ngoài khoảng ⇒ hiện "Hết hiệu lực". */
    validFrom: isoDate.nullish(),
    validTo: isoDate.nullish(),
  })
  .refine((d) => !d.validFrom || !d.validTo || d.validFrom <= d.validTo, { message: 'Ngày hết hiệu lực phải sau ngày bắt đầu', path: ['validTo'] });
export const payInfoInput = z.object({
  label: reqText(80, 'Nhập tiêu đề'),
  text: reqText(1000, 'Nhập nội dung'),
});
/** Bộ tài liệu bán hàng của dự án: tên phương án của giá niêm yết, phương án giá thêm, tài liệu, thông tin đi tiền. */
export const projectKitInput = z
  .object({
    /** Tên phương án của giá niêm yết (`price`), VD "TT tiêu chuẩn". */
    priceLabel: optText(40),
    priceSchemes: z.array(priceSchemeInput).max(ATTR_LIMITS.schemes, `Tối đa ${ATTR_LIMITS.schemes} phương án giá`).default([]),
    documents: z.array(projectDocInput).max(ATTR_LIMITS.projectDocs, `Tối đa ${ATTR_LIMITS.projectDocs} tài liệu`).default([]),
    payInfo: z.array(payInfoInput).max(ATTR_LIMITS.payInfo, `Tối đa ${ATTR_LIMITS.payInfo} mục thông tin thanh toán`).default([]),
  })
  .refine((k) => new Set(k.priceSchemes.map((s) => s.key)).size === k.priceSchemes.length, { message: 'Mã phương án giá bị trùng', path: ['priceSchemes'] });

// ---------- Dữ liệu vào: dự án ----------
export const installmentInput = z.object({
  label: z.preprocess((v) => (v == null ? '' : v), text(200)),
  pct: pct(2),
  days: int().min(0, 'Số ngày không được âm').max(36_500, 'Số ngày quá lớn'),
});
export const milestoneInput = z
  .object({
    type: z.enum(MILESTONE_TYPES, msg('Loại mốc không hợp lệ')),
    pct: pct(2),
    /** numeric(5,2) ⇒ tối đa 2 chữ số thập phân (snapshot sự kiện = dữ liệu đã lưu). */
    threshold: num().min(0, 'Ngưỡng 0–100').max(100, 'Ngưỡng 0–100').refine(decimals(2), 'Ngưỡng tối đa 2 chữ số thập phân').nullish(),
  })
  .refine((m) => m.type !== 'paidPct' || (m.threshold !== null && m.threshold !== undefined), {
    message: 'Mốc "KH thanh toán ≥ X%" cần ngưỡng 0–100',
    path: ['threshold'],
  });
const blocksField = z.preprocess(
  splitComma,
  z
    .array(reqText(40, 'Tên toà không được rỗng'), { invalid_type_error: 'Danh sách toà không hợp lệ' })
    .max(MAX_BLOCKS, 'Tối đa 100 toà / phân khu'),
);
/** Trường của dự án (tạo/sửa dùng chung, KHÔNG có mặc định). Tổng tiến độ = 100% và tổng mốc ≤ 100% kiểm ở service (422 có reason). */
const projectFields = {
  name: reqText(200, 'Nhập tên dự án'),
  developer: optText(200),
  location: optText(200),
  type: z.enum(PROJECT_TYPES, msg('Loại hình không hợp lệ')),
  market: z.enum(MARKETS, msg('Thị trường không hợp lệ')),
  status: z.enum(PROJECT_STATUSES, msg('Trạng thái không hợp lệ')),
  legal: z.preprocess((v) => (v === '' ? null : v), z.enum(LEGAL_TYPES, msg('Pháp lý không hợp lệ')).nullish()),
  floorLabel: z.enum(FLOOR_LABELS, msg('Nhãn tầng không hợp lệ')),
  blocks: blocksField,
  agencyRate: num().min(0, 'Phí môi giới phải ≥ 0').max(100, 'Phí môi giới tối đa 100%').refine(decimals(3), 'Tối đa 3 chữ số thập phân'),
  hotBonus: money,
  bookingAmount: money,
  vatIncluded: bool,
  vatPct: pct(2),
  policy: optText(4000),
  /** Vắng mặt ⇒ DEFAULT_PLAN (P7). */
  paymentPlan: z.array(installmentInput).min(1, 'Cần ít nhất một đợt thanh toán').max(20, 'Tối đa 20 đợt').optional(),
  /** Vắng mặt hoặc rỗng ⇒ DEFAULT_MILESTONES (P7). */
  milestones: z.array(milestoneInput).max(20, 'Tối đa 20 mốc').optional(),
  /** Bộ tài liệu & phương án giá; vắng mặt khi tạo ⇒ rỗng, khi sửa ⇒ giữ nguyên. */
  kit: projectKitInput.optional(),
};
/** Tạo: trường bỏ trống ⇒ mặc định form prototype (đặc tả §2.1). */
export const createProjectInput = z.object({
  ...projectFields,
  type: projectFields.type.default('Căn hộ'),
  market: projectFields.market.default('primary'),
  status: projectFields.status.default('selling'),
  floorLabel: projectFields.floorLabel.default('Tầng'),
  blocks: projectFields.blocks.default(['A', 'B']),
  agencyRate: projectFields.agencyRate.default(3),
  hotBonus: projectFields.hotBonus.default(0),
  bookingAmount: projectFields.bookingAmount.default(50_000_000),
  vatIncluded: projectFields.vatIncluded.default(true),
  vatPct: projectFields.vatPct.default(10),
});
/**
 * PUT (If-Match = version): FE gửi bản đầy đủ của form. Trường vắng mặt = giữ nguyên giá trị hiện tại (KHÔNG áp mặc
 * định lúc tạo); null với trường tuỳ chọn = xoá giá trị.
 */
export const updateProjectInput = z.object(projectFields).partial().extend({ projectId: uuid, expectedVersion: version });
export const requestProjectDeletionInput = z.object({ projectId: uuid, expectedVersion: version });

// ---------- Dữ liệu vào: căn ----------
const ownerInput = z.object({
  ownerName: optText(120),
  /** Lưu sau normPhone (rỗng ⇒ null). */
  ownerPhone: z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? null : v), str().max(40, 'Tối đa 40 ký tự').nullish()),
});
const unitFields = {
  code: reqText(64, 'Nhập mã căn'),
  /** Trống ⇒ toà đầu tiên của dự án. */
  block: optText(40),
  floor: int().min(-10, 'Tầng không hợp lệ').max(500, 'Tầng không hợp lệ'),
  pos: int().min(1, 'Vị trí từ 1').max(1000, 'Vị trí quá lớn').nullish(),
  type: z.preprocess((v) => (v == null ? '' : v), text(60)),
  /** numeric(10,2) ⇒ tối đa 2 chữ số thập phân. */
  area: num().positive('Diện tích phải lớn hơn 0').max(1_000_000, 'Diện tích quá lớn').refine(decimals(2), 'Diện tích tối đa 2 chữ số thập phân').nullish(),
  direction: z.preprocess((v) => (v == null ? '' : v), text(60)),
  view: z.preprocess((v) => (v == null ? '' : v), text(100)),
  price: num('Nhập giá').int('Giá phải là số nguyên').positive('Nhập giá').max(9e15, 'Giá vượt giới hạn'),
  address: optText(300),
  legalNote: optText(500),
  /** Chỉ dự án thứ cấp; cần inventory.owner (vắng mặt = giữ nguyên). */
  owner: ownerInput.optional(),
  /** Thuộc tính bảng hàng CĐT (vắng mặt = giữ nguyên; gửi = thay toàn bộ). */
  attrs: unitAttrsInput.optional(),
};
export const createUnitInput = z.object({ projectId: uuid, ...unitFields, floor: unitFields.floor.default(1), type: unitFields.type.default(''), direction: unitFields.direction.default(''), view: unitFields.view.default('') });
/** PATCH (If-Match = version): trường vắng mặt = giữ nguyên. Giá đổi ⇒ lịch sử giá với priceChangeNote (mặc định "Điều chỉnh giá"). */
export const updateUnitInput = z.object({
  unitId: uuid,
  expectedVersion: version,
  code: unitFields.code.optional(),
  block: unitFields.block,
  floor: unitFields.floor.optional(),
  pos: unitFields.pos,
  type: text(60).optional(),
  area: unitFields.area,
  direction: text(60).optional(),
  view: text(100).optional(),
  price: unitFields.price.optional(),
  address: unitFields.address,
  legalNote: unitFields.legalNote,
  owner: unitFields.owner,
  attrs: unitFields.attrs,
  priceChangeNote: optText(500),
});
export const requestUnitDeletionInput = z.object({ unitId: uuid, expectedVersion: version });

// ---------- Dữ liệu vào: bảng giá & nhập liệu hàng loạt ----------
export const bulkAdjustPricesInput = z.object({
  projectId: uuid,
  unitIds: z.array(uuid).min(1, 'Chọn ít nhất một căn').max(5000, 'Tối đa 5.000 căn mỗi lần'),
  pct: num('Nhập % tăng (số dương) hoặc giảm (số âm), lớn hơn −100')
    .refine((x) => Number.isFinite(x) && x !== 0 && x > -100 && x <= 1000, 'Nhập % tăng (số dương) hoặc giảm (số âm), lớn hơn −100')
    /** price_adjustment.pct numeric(7,3). */
    .refine(decimals(3), 'Tối đa 3 chữ số thập phân'),
});
export const layoutRowInput = z.object({
  pos: int().min(1, 'Vị trí từ 1').max(40, 'Vị trí tối đa 40'),
  type: z.preprocess((v) => (v == null ? '' : v), text(60)),
  area: num().min(0, 'Diện tích không được âm').max(100_000, 'Diện tích quá lớn').refine(decimals(2), 'Diện tích tối đa 2 chữ số thập phân'),
});
/**
 * Tạo nhanh bảng hàng (G1–G7); dùng chung cho xem trước và lệnh. layout hoặc layoutText ("vị trí:loại:diện tích" mỗi dòng).
 * Tổng số căn (≤ INVENTORY_GENERATOR_MAX_UNITS, mặc định 2.000) kiểm ở service ⇒ 422 GENERATOR_TOO_LARGE.
 */
export const generateUnitsInput = z
  .object({
    projectId: uuid,
    block: reqText(40, 'Nhập toà / phân khu'),
    fromFloor: int().min(1, 'Tầng từ 1').max(500, 'Tầng tối đa 500').default(3),
    toFloor: int().min(1, 'Tầng từ 1').max(500, 'Tầng tối đa 500').default(20),
    perFloor: int().min(1, 'Số căn mỗi tầng từ 1').max(40, 'Tối đa 40 căn mỗi tầng').default(8),
    layout: z.array(layoutRowInput).max(40, 'Tối đa 40 dòng mặt bằng').optional(),
    layoutText: text(4000).optional(),
    basePricePerM2: money.default(55_000_000),
    stepPctPerFloor: num().min(-100, 'Bước giá từ −100% đến 100%').max(100, 'Bước giá từ −100% đến 100%').default(0.4),
    pattern: reqText(60, 'Nhập mẫu mã căn').default('{toa}-{tang}.{can}'),
  })
  .refine((g) => g.fromFloor <= g.toFloor, { message: 'Tầng bắt đầu phải nhỏ hơn hoặc bằng tầng kết thúc', path: ['toFloor'] });
export const previewUnitGenerationInput = generateUnitsInput;
/** Văn bản dán từ Excel (dòng tiêu đề + dữ liệu; ≤ 5.000 dòng). */
export const importUnitsInput = z.object({
  projectId: uuid,
  text: reqText(5_000_000, 'Dán các dòng copy từ file CĐT (có dòng tiêu đề)'),
});
export const previewUnitImportInput = importUnitsInput;

// ---------- Truy vấn công khai ----------
export const projectRef = z.object({ projectId: uuid });
export const unitRef = z.object({ unitId: uuid });
export const listProjectsInput = z.object({ includeClosed: boolish(true), market: z.enum(MARKETS, msg('Thị trường không hợp lệ')).optional() });
export const getProjectInput = projectRef;
export const listUnitsInput = z.object({
  projectId: uuid,
  q: text(200).optional(),
  /** Trạng thái hiệu lực. */
  status: z.enum(UNIT_STATUSES, msg('Trạng thái không hợp lệ')).optional(),
  block: text(40).optional(),
  /** Quỹ căn (bảng hàng CĐT); 'none' = căn chưa phân loại quỹ. */
  fund: z.enum([...UNIT_FUNDS, 'none'], msg('Quỹ căn không hợp lệ')).optional(),
  sort: z.enum(UNIT_SORTS, msg('Cách sắp xếp không hợp lệ')).default('code'),
  dir: z.enum(['asc', 'desc'], msg('Chiều sắp xếp không hợp lệ')).default('asc'),
  limit: qInt(1, 500, 500),
  cursor: text(200).optional(),
});
export const getUnitInput = unitRef;
export const getUnitOwnerInput = unitRef;
export const listUnitPriceHistoryInput = z.object({ unitId: uuid, limit: qInt(1, 200, 20) });
/** Chiết khấu ngoài 0–20 bị kẹp như giao diện (IN39). */
export const getUnitQuoteInput = z.object({
  unitId: uuid,
  discountPct: z.coerce
    .number({ invalid_type_error: 'Chiết khấu phải là số' })
    .default(0)
    .transform((x) => Math.max(0, Math.min(QUOTE_DISCOUNT_MAX, x || 0))),
});
export const searchUnitsInput = z.object({ q: reqText(200, 'Nhập từ khoá'), limit: qInt(1, 20, 6) });
export const listUnitTypesInput = z.object({ projectId: uuid.optional() });
export const getInventoryOverviewInput = z.object({});
export const getDeletionRequestInput = z.object({ deletionRequestId: uuid });

// ---------- Truy vấn nội bộ ----------
export const getProjectPolicySnapshotInput = z.object({
  projectIds: z.array(uuid).max(500, 'Tối đa 500 dự án').optional(),
  updatedSince: isoTime.optional(),
  afterProjectId: uuid.optional(),
  limit: qInt(1, 1000, 500),
  /** true ⇒ gồm cả dự án đã xoá (deletedAt ≠ null) để bên đối soát gỡ khỏi projection. */
  includeDeleted: boolish(false),
});
export const getUnitCatalogSnapshotInput = z.object({ projectId: uuid.optional(), afterUnitId: uuid.optional(), limit: qInt(1, 1000, 500), includeDeleted: boolish(false) });

/** Tên RPC: inventory.cmd.<Name> / inventory.qry.<Name>. */
export const RPC = {
  cmd: [
    'CreateProject',
    'UpdateProject',
    'RequestProjectDeletion',
    'CreateUnit',
    'UpdateUnit',
    'RequestUnitDeletion',
    'BulkAdjustPrices',
    'GenerateUnits',
    'ImportUnits',
    'SetProjectShowroom',
  ],
  qry: [
    'ListProjects',
    'GetProject',
    'ListUnits',
    'GetUnit',
    'GetUnitOwner',
    'ListUnitPriceHistory',
    'GetUnitQuote',
    'SearchUnits',
    'ListUnitTypes',
    'GetInventoryOverview',
    'PreviewUnitGeneration',
    'PreviewUnitImport',
    'GetDeletionRequest',
    'GetProjectShowroom',
    // nội bộ
    'GetProjectPolicySnapshot',
    'GetUnitCatalogSnapshot',
  ],
} as const;

// ---------- DTO / snapshot ----------
export interface PaymentInstallment {
  label: string;
  pct: number;
  days: number;
}
export interface CommissionMilestone {
  type: MilestoneType;
  pct: number;
  /** Chỉ với paidPct. */
  threshold?: number | null;
}
export interface UnitDoc {
  kind: UnitDocKind;
  title: string;
  url: string;
}
/** Thuộc tính bảng hàng CĐT của căn — luôn là object ({} khi không có); trường vắng mặt = không có dữ liệu. */
export interface UnitAttrs {
  /** Trục / số căn trên mặt bằng tầng ("05A") — tiêu đề cột của lưới bảng hàng. */
  axis?: string | null;
  /** Phân khu (VD "The Bloom") khi toà là cấp nhỏ hơn. */
  zone?: string | null;
  fund?: UnitFund | null;
  /** Đại lý đang giữ / đã bán căn theo bảng hàng CĐT. */
  agency?: string | null;
  /** Giá theo phương án thanh toán (khoá = ProjectKit.priceSchemes[].key), VND. */
  prices?: Record<string, number>;
  /** Giá thuần chưa gồm VAT & KPBT, VND. */
  netPrice?: number | null;
  docs?: UnitDoc[];
}
export interface PriceScheme {
  key: string;
  label: string;
}
export interface ProjectDoc {
  category: ProjectDocCategory;
  title: string;
  url: string;
  block?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
}
export interface PayInfo {
  label: string;
  text: string;
}
/** Bộ tài liệu bán hàng của dự án — luôn đầy đủ (mảng rỗng khi chưa khai báo). */
export interface ProjectKit {
  priceLabel: string | null;
  priceSchemes: PriceScheme[];
  documents: ProjectDoc[];
  payInfo: PayInfo[];
}
export const emptyKit = (): ProjectKit => ({ priceLabel: null, priceSchemes: [], documents: [], payInfo: [] });

/** Ảnh chụp chính sách dự án — payload sự kiện và kết quả GetProjectPolicySnapshot. */
export interface ProjectSnapshot {
  projectId: string;
  name: string;
  developer: string | null;
  location: string | null;
  type: ProjectType;
  market: Market;
  status: ProjectStatus;
  legal: string | null;
  floorLabel: FloorLabel;
  blocks: string[];
  /** % phí môi giới CĐT trả sàn. */
  agencyRate: number;
  hotBonus: number;
  bookingAmount: number;
  vatIncluded: boolean;
  vatPct: number;
  policy: string | null;
  /** Luôn đầy đủ (rỗng đã thay bằng DEFAULT_PLAN khi lưu). */
  paymentPlan: PaymentInstallment[];
  /** Luôn đầy đủ (rỗng đã thay bằng DEFAULT_MILESTONES khi lưu). */
  milestones: CommissionMilestone[];
  /** Bộ tài liệu bán hàng — inventory luôn gửi; vắng mặt ở sự kiện phát trước khi có bộ tài liệu. */
  kit?: ProjectKit;
  lifecycle: Lifecycle;
  createdAt: string;
  version: number;
}
export type ProjectField = Exclude<keyof ProjectSnapshot, 'projectId' | 'lifecycle' | 'createdAt' | 'version'>;
/** Danh mục công khai của căn (không có thông tin chủ nhà). */
export interface UnitPublicSnapshot {
  unitId: string;
  projectId: string;
  code: string;
  block: string;
  floor: number;
  pos: number | null;
  type: string;
  area: number | null;
  direction: string;
  view: string;
  price: number;
  priceVersion: number;
  address: string | null;
  legalNote: string | null;
  consignedAt: string | null;
  source: UnitSource;
  /** Thuộc tính bảng hàng CĐT — inventory luôn gửi ({} khi không có); vắng mặt ở sự kiện phát trước khi có trường này. */
  attrs?: UnitAttrs;
  version: number;
}
export type UnitField = Exclude<keyof UnitPublicSnapshot, 'unitId' | 'projectId' | 'priceVersion' | 'source' | 'version'> | 'owner';
export type UnitStatusCounts = Record<UnitStatus, number>;
export interface ProjectCard {
  projectId: string;
  name: string;
  developer: string | null;
  location: string | null;
  type: ProjectType;
  market: Market;
  status: ProjectStatus;
  agencyRate: number;
  unitCount: number;
  counts: UnitStatusCounts;
  sellable: number;
  sold: number;
  /** Gateway gộp từ deal GetDealTotalsByProject (Σ value giao dịch LIVE). */
  liveValue?: number;
  /** Số giao dịch từng gắn vào căn của dự án — cùng bộ đếm với GetProject.dealCount và luật xoá P11 (một nguồn duy nhất). */
  dealCount?: number;
  createdAt: string;
}
export interface ProjectList {
  items: ProjectCard[];
  serverTime: string;
}
export interface ProjectDetail extends ProjectSnapshot {
  kit: ProjectKit;
  unitCount: number;
  dealCount: number;
  updatedAt: string;
}
export interface CreateProjectResult {
  projectId: string;
  version: number;
  /** VD "Mốc hoa hồng mới cộng 50% — phần còn lại sẽ không được chi". */
  warnings: string[];
}
export interface UpdateProjectResult {
  version: number;
  changedFields: ProjectField[];
  warnings: string[];
}
export interface UnitRow {
  unitId: string;
  code: string;
  block: string;
  floor: number;
  pos: number | null;
  type: string;
  area: number | null;
  direction: string;
  view: string;
  price: number;
  pricePerM2: number;
  address: string | null;
  legalNote: string | null;
  consignedAt: string | null;
  hasPriceHistory: boolean;
  effectiveStatus: UnitStatus;
  hasDeal: boolean;
  attrs: UnitAttrs;
  version: number;
}
export interface UnitList {
  items: UnitRow[];
  blocks: string[];
  total: number;
  nextCursor: string | null;
  serverTime: string;
}
export interface UnitDetail extends UnitPublicSnapshot {
  attrs: UnitAttrs;
  pricePerM2: number;
  effectiveStatus: UnitStatus;
  hasDeal: boolean;
  lifecycle: Lifecycle;
  project: { id: string; name: string; market: Market; floorLabel: FloorLabel; blocks: string[]; status: ProjectStatus };
}
export interface UnitOwnerView {
  ownerName: string | null;
  ownerPhone: string | null;
}
export interface PriceHistoryEntry {
  at: string;
  from: number;
  to: number;
  note: string;
  source: PriceChangeSource;
}
export interface UnitQuote {
  listPrice: number;
  discountPct: number;
  value: number;
  pricePerM2: number;
  bookingAmount: number;
  installments: { label: string; pct: number; amount: number }[];
  policy: string | null;
  /** quoteText — không emoji. */
  text: string;
}
export interface UnitSearchHit {
  unitId: string;
  code: string;
  projectId: string;
  projectName: string;
  type: string;
  effectiveStatus: UnitStatus;
  price: number;
}
export interface InventoryOverview {
  projectCount: number;
  unitCount: number;
}
export interface UnitPlanSample {
  code: string;
  type: string;
  area: number;
  price: number;
}
export interface UnitGenerationPreview {
  total: number;
  toCreate: number;
  skippedExisting: number;
  skippedMissingLayout: number;
  first: UnitPlanSample | null;
  last: UnitPlanSample | null;
}
export interface UnitGenerationResult {
  batchId: string;
  created: number;
  skipped: { code: string; reason: 'CODE_EXISTS' | 'MISSING_LAYOUT' }[];
  blockAdded: boolean;
}
export interface UnitImportRowPreview {
  row: number;
  code: string;
  block: string;
  floor: number;
  /** Trục ("05A") — chỉ có khi file có cột Trục. */
  axis?: string | null;
  type: string;
  area: number | null;
  price: number | null;
  /** Quỹ đọc từ cột Tình trạng (undefined khi file không có cột này). */
  fund?: UnitFund | null;
  /** Giá theo phương án đọc được ở dòng này (khoá phương án ⇒ VND). */
  prices?: Record<string, number>;
  classification: ImportClassification;
}
export type ImportColumnKey = 'code' | 'block' | 'floor' | 'pos' | 'type' | 'area' | 'direction' | 'view' | 'price' | 'address' | 'axis' | 'zone' | 'fund' | 'agency' | 'netPrice';
export interface UnitImportPreview {
  /** Chỉ số cột nhận diện (I2); thiếu cột ⇒ không có khoá. */
  columns: Partial<Record<ImportColumnKey, number>>;
  /** Cột giá theo phương án: khoá phương án ⇒ chỉ số cột. */
  schemeColumns: Record<string, number>;
  /** Dòng tiêu đề (1 = dòng đầu văn bản dán) — dòng phía trên (tham số, tiêu đề bảng) được bỏ qua. */
  headerRow: number;
  /** Đơn vị giá suy ra: 'vnd' hoặc 'million' (giá ghi bằng triệu đồng, VD 10058 = 10,058 tỷ). */
  priceUnit: 'vnd' | 'million';
  /** ≤ 300 dòng đầu. */
  rows: UnitImportRowPreview[];
  counts: Record<ImportClassification, number>;
  /** Số dòng sẽ được ghi (CREATE + PRICE_UPDATE). */
  actionable: number;
}
export interface UnitImportResult {
  batchId: string;
  created: number;
  priceUpdated: number;
  /** Căn chỉ đổi quỹ / thông tin (INFO_UPDATE) hoặc đổi giá kèm thông tin. */
  infoUpdated: number;
  unchanged: number;
  skipped: { row: number; code: string; reason: ImportClassification }[];
}
export interface PriceAdjustmentResult {
  adjustmentId: string;
  adjusted: number;
  skipped: { unitId: string; code: string | null; reason: 'HAS_DEAL' | 'NOT_IN_PROJECT' | 'UNCHANGED' | 'DELETING' }[];
}
export interface DeletionRequestView {
  deletionRequestId: string;
  kind: DeletionKind;
  targetId: string;
  projectId: string;
  state: DeletionState;
  reasonCode: string | null;
  message: string | null;
}
export interface RequestDeletionResult {
  deletionRequestId: string;
}
export interface CreateUnitResult {
  unitId: string;
  version: number;
}
export interface UpdateUnitResult {
  version: number;
  priceVersion: number;
  changedFields: UnitField[];
}
/** ListUnitTypes: loại căn khác nhau, sắp tăng dần. */
export type UnitTypeList = string[];
/** Trang của GetProjectPolicySnapshot (nội bộ) — sắp theo id; mặc định chỉ dự án chưa xoá (includeDeleted ⇒ gồm cả đã xoá). */
export interface ProjectPolicySnapshotPage {
  /** deletedAt: null = còn sống; ISO = đã xoá (chỉ có khi includeDeleted). */
  items: (ProjectSnapshot & { deletedAt?: string | null })[];
  nextAfterProjectId: string | null;
}
/** Trang của GetUnitCatalogSnapshot (nội bộ) — sắp theo id, không có chủ nhà; mặc định chỉ căn chưa xoá. */
export interface UnitCatalogSnapshotPage {
  items: (UnitPublicSnapshot & { deletedAt?: string | null })[];
  nextAfterUnitId: string | null;
}

// ---------- Sự kiện ----------
export const EVENTS = {
  projectCreated: 'inventory.project.created',
  projectUpdated: 'inventory.project.updated',
  projectStatusChanged: 'inventory.project.statusChanged',
  projectDeletionRequested: 'inventory.project.deletionRequested',
  projectDeletionRejected: 'inventory.project.deletionRejected',
  projectDeleted: 'inventory.project.deleted',
  projectShowroomUpdated: 'inventory.project.showroomUpdated',
  unitCreated: 'inventory.unit.created',
  unitUpdated: 'inventory.unit.updated',
  unitBatchCreated: 'inventory.unitBatch.created',
  priceListAdjusted: 'inventory.priceList.adjusted',
  unitImportCompleted: 'inventory.unitImport.completed',
  unitAttrsUpdated: 'inventory.unitAttrs.updated',
  unitDeletionRequested: 'inventory.unit.deletionRequested',
  unitDeletionRejected: 'inventory.unit.deletionRejected',
  unitDeleted: 'inventory.unit.deleted',
  unitOwnerViewed: 'inventory.unitOwner.viewed',
  deletionRequestStalled: 'inventory.deletionRequest.stalled',
} as const;
export type EventType = (typeof EVENTS)[keyof typeof EVENTS];

/** Chia lô sự kiện lớn (căn tạo nhanh, điều chỉnh giá, xoá dự án). */
export interface Chunk {
  index: number;
  total: number;
}
export interface ProjectCreated {
  project: ProjectSnapshot;
}
export interface ProjectUpdated {
  project: ProjectSnapshot;
  changedFields: ProjectField[];
  /** Giá trị cũ của các trường đổi. */
  previous: Partial<ProjectSnapshot>;
}
export interface ProjectStatusChanged {
  projectId: string;
  from: ProjectStatus;
  to: ProjectStatus;
}
/** Yêu cầu booking rút toàn bộ căn của dự án (bước D2). Thử lại = sự kiện mới, cùng idempotencyKey. */
export interface ProjectDeletionRequested {
  deletionRequestId: string;
  idempotencyKey: string; // '<deletionRequestId>:withdraw'
  attempt: number;
  projectId: string;
  requestedBy: string | null;
}
export interface ProjectDeletionRejected {
  deletionRequestId: string;
  projectId: string;
  name: string;
  reasonCode: string;
  message: string;
  requestedBy: string | null;
}
export interface ProjectDeleted {
  projectId: string;
  name: string;
  /** Căn bị xoá theo dự án (chia lô 1.000). */
  unitIds: string[];
  chunk: Chunk;
}
export interface UnitCreated {
  unit: UnitPublicSnapshot;
}
export interface UnitUpdated {
  unit: UnitPublicSnapshot;
  changedFields: UnitField[];
  priceChange: { from: number; to: number; note: string; priceVersion: number } | null;
}
export interface UnitBatchCreated {
  batchId: string;
  projectId: string;
  source: 'generator' | 'import';
  chunk: Chunk;
  /** ≤ 500 căn mỗi sự kiện. */
  units: UnitPublicSnapshot[];
}
export interface PriceListAdjusted {
  adjustmentId: string;
  projectId: string;
  source: 'bulk_adjust' | 'import';
  pct: number | null;
  note: string;
  chunk: Chunk;
  items: { unitId: string; code: string; from: number; to: number; priceVersion: number }[];
}
/** Thuộc tính bảng hàng của nhiều căn đổi (nhập lại bảng hàng CĐT — cập nhật quỹ); ≤ 500 căn mỗi sự kiện. */
/** Nội dung Trình chiếu đổi (chỉ tên mục — không mang nội dung để sự kiện nhỏ). */
export interface ProjectShowroomUpdated {
  projectId: string;
  version: number;
  /** Mục cấp 1 của ProjectShowroom đã đổi (VD "gallery", "schemes"). */
  sections: string[];
}
export interface UnitAttrsUpdated {
  batchId: string;
  projectId: string;
  chunk: Chunk;
  items: { unitId: string; code: string; attrs: UnitAttrs; version: number }[];
}
export interface UnitImportCompleted {
  batchId: string;
  projectId: string;
  created: number;
  priceUpdated: number;
  /** Căn chỉ cập nhật quỹ / thông tin — vắng mặt ở sự kiện phát trước khi có bảng hàng CĐT. */
  infoUpdated?: number;
  unchanged: number;
  skipped: { reason: ImportClassification; count: number }[];
}
export interface UnitDeletionRequested {
  deletionRequestId: string;
  idempotencyKey: string; // '<deletionRequestId>:withdraw'
  attempt: number;
  unitId: string;
  projectId: string;
  requestedBy: string | null;
}
export interface UnitDeletionRejected {
  deletionRequestId: string;
  unitId: string;
  projectId: string;
  code: string;
  reasonCode: string;
  message: string;
  requestedBy: string | null;
}
export interface UnitDeleted {
  unitId: string;
  projectId: string;
  code: string;
}
/** Truy cập nhạy cảm — không chứa tên/SĐT chủ nhà. Ghi MỌI lần xem (kể cả ngữ cảnh hệ thống / tài khoản chưa có nhân sự). */
export interface UnitOwnerViewed {
  unitId: string;
  projectId: string;
  /** null = ngữ cảnh hệ thống hoặc tài khoản chưa gắn nhân sự. */
  viewerStaffId: string | null;
  /** Tài khoản người xem (null = hệ thống). */
  viewerUserId: string | null;
}
export interface DeletionRequestStalled {
  deletionRequestId: string;
  kind: DeletionKind;
  targetId: string;
  projectId: string;
  /** Tên dự án hoặc mã căn (tiêu đề mục vận hành). */
  label: string;
  attempt: number;
}

export interface EventPayloads {
  [EVENTS.projectCreated]: ProjectCreated;
  [EVENTS.projectUpdated]: ProjectUpdated;
  [EVENTS.projectStatusChanged]: ProjectStatusChanged;
  [EVENTS.projectDeletionRequested]: ProjectDeletionRequested;
  [EVENTS.projectDeletionRejected]: ProjectDeletionRejected;
  [EVENTS.projectDeleted]: ProjectDeleted;
  [EVENTS.projectShowroomUpdated]: ProjectShowroomUpdated;
  [EVENTS.unitCreated]: UnitCreated;
  [EVENTS.unitUpdated]: UnitUpdated;
  [EVENTS.unitBatchCreated]: UnitBatchCreated;
  [EVENTS.priceListAdjusted]: PriceListAdjusted;
  [EVENTS.unitImportCompleted]: UnitImportCompleted;
  [EVENTS.unitAttrsUpdated]: UnitAttrsUpdated;
  [EVENTS.unitDeletionRequested]: UnitDeletionRequested;
  [EVENTS.unitDeletionRejected]: UnitDeletionRejected;
  [EVENTS.unitDeleted]: UnitDeleted;
  [EVENTS.unitOwnerViewed]: UnitOwnerViewed;
  [EVENTS.deletionRequestStalled]: DeletionRequestStalled;
}
type AssertNever<T extends never> = T;
/** Lỗi biên dịch nếu có sự kiện trong EVENTS chưa khai báo payload. */
export type _AllEventsHavePayload = AssertNever<Exclude<EventType, keyof EventPayloads>>;
